import React, { useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, XCircle } from 'lucide-react';

interface WorkflowMetrics {
  total_executions_24h: number;
  failed_executions_24h: number;
  avg_duration_ms: number | null;
  active_executions: number;
  dead_letter_count: number;
  idempotent_skips: number;
  concurrency_limit: number;
  timeout_count: number;
}

interface DeadLetterEntry {
  execution_id: string;
  workflow_id: string;
  workspace_id: string;
  status: string;
  error_message: string | null;
  dlq_reason: string | null;
  timestamp: string | null;
  trigger_type: string | null;
}

export const WorkflowHealthPage: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [metrics, setMetrics] = useState<WorkflowMetrics | null>(null);
  const [dlqEntries, setDlqEntries] = useState<DeadLetterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [redriving, setRedriving] = useState<string | null>(null);

  const fetchWorkspaces = async () => {
    try {
      const res = await fastapiService.getWorkspaces({ limit: 200 });
      const list = res.data || [];
      setWorkspaces(list.map((w: any) => ({ id: w.id, name: w.name || w.id })));
      if (list.length > 0 && !selectedWorkspaceId) {
        setSelectedWorkspaceId(list[0].id);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load workspaces');
    }
  };

  const fetchMetrics = async () => {
    if (!selectedWorkspaceId) return;
    try {
      setLoading(true);
      setError('');
      const m = await fastapiService.getWorkflowMetrics(selectedWorkspaceId);
      setMetrics(m);
    } catch (e: any) {
      setError(e?.message || 'Failed to load metrics');
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchDlq = async () => {
    try {
      const entries = await fastapiService.getWorkflowDeadLetter(selectedWorkspaceId || undefined);
      setDlqEntries(Array.isArray(entries) ? entries : []);
    } catch (e: any) {
      console.error('Failed to load DLQ:', e);
      setDlqEntries([]);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    if (selectedWorkspaceId) {
      fetchMetrics();
      fetchDlq();
    }
  }, [selectedWorkspaceId]);

  const handleRedrive = async (executionId: string) => {
    try {
      setRedriving(executionId);
      await fastapiService.redriveWorkflowExecution(executionId);
      await fetchDlq();
      await fetchMetrics();
    } catch (e: any) {
      alert(e?.message || 'Redrive failed');
    } finally {
      setRedriving(null);
    }
  };

  const failureRate = metrics && metrics.total_executions_24h > 0
    ? (metrics.failed_executions_24h / metrics.total_executions_24h) * 100
    : 0;
  const healthColor = failureRate < 2 ? 'green' : failureRate < 5 ? 'yellow' : 'red';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-900">Workflow Health</h1>
          <p className="mt-1 text-sm text-admin-600">
            Per-workspace workflow execution metrics and dead-letter queue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedWorkspaceId}
            onChange={(e) => setSelectedWorkspaceId(e.target.value)}
            className="admin-input w-64"
          >
            <option value="">Select workspace</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <button
            onClick={() => { fetchMetrics(); fetchDlq(); }}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && selectedWorkspaceId ? (
        <p className="text-admin-500">Loading metrics...</p>
      ) : metrics && selectedWorkspaceId ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <Activity className="w-4 h-4" />
                Active Executions
              </div>
              <div className="mt-2 text-2xl font-bold text-admin-900">
                {metrics.active_executions} / {metrics.concurrency_limit}
              </div>
            </div>
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <XCircle className="w-4 h-4" />
                Failures (24h)
              </div>
              <div className={`mt-2 text-2xl font-bold ${
                healthColor === 'green' ? 'text-green-600' :
                healthColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {metrics.failed_executions_24h} / {metrics.total_executions_24h}
              </div>
              <div className="text-xs text-admin-500 mt-1">
                {failureRate.toFixed(1)}% failure rate
              </div>
            </div>
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <Clock className="w-4 h-4" />
                Avg Execution Time
              </div>
              <div className="mt-2 text-2xl font-bold text-admin-900">
                {metrics.avg_duration_ms != null
                  ? `${Math.round(metrics.avg_duration_ms)}ms`
                  : '-'}
              </div>
            </div>
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                DLQ Count
              </div>
              <div className="mt-2 text-2xl font-bold text-red-600">
                {metrics.dead_letter_count}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                Idempotent Skips (24h)
              </div>
              <div className="mt-2 text-xl font-bold text-admin-900">
                {metrics.idempotent_skips}
              </div>
            </div>
            <div className="admin-card p-4">
              <div className="flex items-center gap-2 text-admin-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                Timeout Count (24h)
              </div>
              <div className="mt-2 text-xl font-bold text-admin-900">
                {metrics.timeout_count}
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className="admin-card p-6">
        <h2 className="text-lg font-semibold text-admin-900 mb-4">Dead Letter Queue</h2>
        {dlqEntries.length === 0 ? (
          <p className="text-admin-500">No DLQ entries</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-admin-200">
              <thead className="bg-admin-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-admin-500">
                  <th className="px-4 py-3">Execution ID</th>
                  <th className="px-4 py-3">Workflow</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-admin-200">
                {dlqEntries.map((e) => (
                  <tr key={e.execution_id} className="hover:bg-admin-50">
                    <td className="px-4 py-3 text-sm font-mono text-admin-700 truncate max-w-[180px]">
                      {e.execution_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-700">{e.workflow_id}</td>
                    <td className="px-4 py-3 text-sm text-red-700 max-w-[200px] truncate" title={e.dlq_reason || e.error_message || ''}>
                      {e.dlq_reason || e.error_message || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-500">
                      {e.timestamp ? new Date(e.timestamp).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRedrive(e.execution_id)}
                        disabled={redriving === e.execution_id}
                        className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 disabled:opacity-50"
                      >
                        {redriving === e.execution_id ? 'Re-driving...' : 'Re-drive'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
